import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { getPublishedPost } from '../services/blogService.ts';
import type { BlogPost } from '../types/blog.ts';

interface StructuredData {
  "@context": string;
  "@type": string;
  headline: string;
  description: string;
  author: {
    "@type": string;
    name: string;
  };
  publisher: {
    "@type": string;
    name: string;
    url: string;
  };
  datePublished: string | null;
  dateModified: string;
  mainEntityOfPage: {
    "@type": string;
    "@id": string;
  };
  keywords: string;
  image?: string;
}

export default function BlogPostScreen() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[BlogPostScreen] Component mounted, slug:', slug);
    if (slug) {
      loadPost();
    }
  }, [slug]);

  const loadPost = async () => {
    if (!slug) return;
    
    try {
      console.log('[BlogPostScreen] Loading post:', slug);
      setLoading(true);
      setError(null);
      const data = await getPublishedPost(slug);
      if (!data) {
        setError('Post not found');
        return;
      }
      setPost(data);
      console.log('[BlogPostScreen] Loaded post:', data.title);
      
      // Only update DOM if we're in browser environment
      if (typeof window !== 'undefined') {
        // Update page title and meta description for SEO
        document.title = `${data.title} | SafeVibe Security Blog`;
        
        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', data.excerpt || `Read ${data.title} on the SafeVibe Security Blog`);
        }
        
        // Add JSON-LD structured data for SEO
        addStructuredData(data);
      }
    } catch (err) {
      console.error('[BlogPostScreen] Error loading post:', err);
      setError(err instanceof Error ? err.message : 'Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const addStructuredData = (post: BlogPost) => {
    if (typeof window === 'undefined') return;

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt,
      "author": {
        "@type": "Organization",
        "name": post.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "SafeVibe",
        "url": "https://safevibe.dev"
      },
      "datePublished": post.published_at,
      "dateModified": post.updated_at,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://safevibe.dev/blog/${post.slug}`
      },
      "keywords": post.tags.join(", "),
      ...(post.cover_url && { image: post.cover_url })
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatReadTime = (content: string) => {
    const wordCount = content.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / 200);
    return `${readTime} min read`;
  };

  const renderMarkdown = (markdown: string) => {
    // Enhanced markdown rendering with better styling
    return markdown
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return <h1 key={index} className="text-4xl font-bold text-gray-900 mb-6 leading-tight">{line.slice(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-3xl font-semibold text-gray-900 mb-4 mt-8 leading-tight">{line.slice(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-2xl font-medium text-gray-900 mb-3 mt-6 leading-tight">{line.slice(4)}</h3>;
        }
        if (line.startsWith('#### ')) {
          return <h4 key={index} className="text-xl font-medium text-gray-900 mb-2 mt-4">{line.slice(5)}</h4>;
        }
        if (line.trim() === '') {
          return <div key={index} className="h-4" />;
        }
        if (line.startsWith('![')) {
          // Image handling
          const match = line.match(/!\[([^\]]*)\]\(([^\)]+)\)/);
          if (match) {
            const [, alt, src] = match;
            return (
              <div key={index} className="my-8">
                <img 
                  src={src} 
                  alt={alt} 
                  className="w-full rounded-lg shadow-md"
                />
                {alt && <p className="text-sm text-gray-600 text-center mt-2 italic">{alt}</p>}
              </div>
            );
          }
        }
        if (line.startsWith('- ') || line.startsWith('* ')) {
          return <li key={index} className="ml-6 mb-1 list-disc">{line.slice(2)}</li>;
        }
        if (line.includes('`')) {
          // Inline code handling
          const parts = line.split('`');
          return (
            <p key={index} className="mb-4 text-gray-700 leading-relaxed">
              {parts.map((part, i) => 
                i % 2 === 0 ? part : <code key={i} className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{part}</code>
              )}
            </p>
          );
        }
        return <p key={index} className="mb-4 text-gray-700 leading-relaxed">{line}</p>;
      });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        // Reset page title
        document.title = 'SafeVibe - Security Scanner for Web Apps';
        // Remove structured data
        const existingScript = document.querySelector('script[type="application/ld+json"]');
        if (existingScript) {
          existingScript.remove();
        }
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading article...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
            <p className="text-gray-600 mb-8">
              {error || 'The blog post you\'re looking for doesn\'t exist or has been removed.'}
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              View All Posts
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        {/* Breadcrumb Navigation */}
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-gray-900">Home</Link>
              <span className="mx-2">→</span>
              <Link to="/blog" className="hover:text-gray-900">Blog</Link>
              <span className="mx-2">→</span>
              <span className="text-gray-900">{post.title}</span>
            </div>
          </div>
        </div>

        {/* Article Header */}
        <header className="bg-white py-12">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-600 mb-4">
                <span>{formatDate(post.published_at)}</span>
                <span>•</span>
                <span>{post.author}</span>
                <span>•</span>
                <span>{formatReadTime(post.content_md)}</span>
              </div>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                {post.title}
              </h1>
              
              {post.excerpt && (
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                  {post.excerpt}
                </p>
              )}

              {post.tags.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Cover Image */}
        {post.cover_url && (
          <div className="container mx-auto px-6 mb-12">
            <div className="max-w-4xl mx-auto">
              <img
                src={post.cover_url}
                alt={post.title}
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        )}

        {/* Article Content */}
        <article className="bg-white py-12">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none">
                {renderMarkdown(post.content_md)}
              </div>
            </div>
          </div>
        </article>

        {/* Article Footer CTA */}
        <section className="bg-blue-600 py-12">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Secure Your Application Today
              </h2>
              <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
                Ready to find vulnerabilities in your own code? 
                Try SafeVibe's free security scanner and get results in under 30 seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
                >
                  Start Free Scan
                </Link>
                <Link
                  to="/blog"
                  className="inline-flex items-center px-8 py-4 border border-white text-lg font-medium rounded-md text-white hover:bg-blue-700 transition-colors"
                >
                  Read More Articles
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
} 